// pages/TranslationVerificationPage.jsx - Public verification page for stamped translations
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import useApiClient from '../services/api';
import Spinner from '../components/Spinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const TranslationVerificationPage = () => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const hashFromUrl = searchParams.get('hash');
  const apiClient = useApiClient();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [translations, setTranslations] = useState([]);
  const [project, setProject] = useState(null);
  const [pdfCheck, setPdfCheck] = useState({
    status: 'idle',
    fileName: '',
    hash: '',
    error: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) {
        setError('No project ID provided.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const projectRes = await apiClient.get(`/projects/public/${projectId}`, {
          params: hashFromUrl ? { hash: hashFromUrl } : {}
        });
        const projectData = projectRes.data?.project || projectRes.data;
        setProject(projectData);

        const transRes = await apiClient.get(`/translations/public/by-project/${projectId}`);
        const transData = transRes.data?.translations || transRes.data || [];
        setTranslations(Array.isArray(transData) ? transData : []);
      } catch (err) {
        console.error('Error fetching verification data:', err);
        if (err.response?.status === 404) {
          setError('Project not found. The link may be invalid or the document may have been removed.');
        } else if (err.response?.status === 403) {
          setError('This document is not publicly accessible.');
        } else {
          setError('Unable to load document details. Please try again or contact support.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
        <div className="text-center">
          <Spinner />
          <p className="mt-4 text-gray-600 text-sm">Verifying document…</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
        <div className="w-full max-w-sm p-6 bg-white rounded-xl shadow-lg text-center">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-800 mb-2">Verification Failed</h2>
          <p className="text-sm text-gray-600 mb-6">{error}</p>
          <a
            href="/"
            className="inline-block w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
          >
            Go to Homepage
          </a>
        </div>
      </div>
    );
  }

  const isVerified = translations.length > 0;
  const firstTranslation = translations[0];
  const translatorName = firstTranslation?.translator?.name || 'N/A';
  const sourceLanguage = firstTranslation?.sourceLanguage || project?.sourceLanguage || 'N/A';
  const targetLanguage = firstTranslation?.targetLanguage || project?.targetLanguage || 'N/A';
  const completedAt = firstTranslation?.completedAt;
  const documentHolder = project?.clientName || project?.name || 'N/A';
  const finalPdfSha256 = project?.finalDocument?.finalPdfSha256 || project?.finalPdfSha256 || null;
  const signatureVerification = project?.finalDocument?.signatureVerification || project?.signatureVerification || null;
  const signatureStatus = signatureVerification?.status || null;
  const finalDocumentType = project?.finalDocument?.type || 'project';
  const finalDocumentId = project?.finalDocument?.id || null;
  const signedDownloadUrl = projectId
    ? (
        finalDocumentType === 'translation' && finalDocumentId
          ? `${API_URL}/projects/public/${projectId}/translation-final-documents/${finalDocumentId}/download`
          : `${API_URL}/projects/public/${projectId}/final-documents/latest/download`
      )
    : null;
  const signedPreviewUrl = projectId
    ? (
        finalDocumentType === 'translation' && finalDocumentId
          ? `${API_URL}/projects/public/${projectId}/translation-final-documents/${finalDocumentId}/preview`
          : `${API_URL}/projects/public/${projectId}/final-documents/latest/preview`
      )
    : null;

  const handlePdfVerification = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setPdfCheck({ status: 'idle', fileName: '', hash: '', error: '' });
      return;
    }

    if (!finalPdfSha256) {
      setPdfCheck({
        status: 'limited',
        fileName: file.name,
        hash: '',
        error: 'Full PDF integrity verification is not available for this older document.',
      });
      return;
    }

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setPdfCheck({
        status: 'error',
        fileName: file.name,
        hash: '',
        error: 'Please select a PDF file.',
      });
      return;
    }

    try {
      setPdfCheck({ status: 'checking', fileName: file.name, hash: '', error: '' });
      const fileBytes = await file.arrayBuffer();
      const digest = await window.crypto.subtle.digest('SHA-256', fileBytes);
      const hash = Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
      const matches = hash.toLowerCase() === finalPdfSha256.toLowerCase();

      setPdfCheck({
        status: matches ? 'match' : 'mismatch',
        fileName: file.name,
        hash,
        error: '',
      });
    } catch (hashError) {
      console.error('Error hashing selected PDF:', hashError);
      setPdfCheck({
        status: 'error',
        fileName: file.name,
        hash: '',
        error: 'Unable to read and hash this PDF. Please try again.',
      });
    }
  };

  // ── Page ─────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* ── Header ── */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3 sm:py-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3 sm:px-4">
            <div className="flex items-center justify-center gap-4 sm:gap-8 flex-wrap">
              <div className="flex items-center justify-center h-10 sm:h-12 min-w-[150px]">
                <img
                  src="/assets/logos/wetranslate.png"
                  alt="WeTranslate"
                  className="h-8 sm:h-9 w-auto object-contain"
                  loading="lazy"
                />
              </div>
              <div className="flex items-center justify-center h-10 sm:h-12 min-w-[110px]">
                <img
                  src="/assets/logos/eidas.svg"
                  alt="eIDAS"
                  className="h-7 sm:h-8 w-auto object-contain"
                  loading="lazy"
                />
              </div>
              <div className="flex items-center justify-center h-10 sm:h-12 min-w-[150px]">
                <img
                  src="/assets/logos/certeurope.svg"
                  alt="CertEurope"
                  className="h-7 sm:h-8 w-auto object-contain"
                  loading="lazy"
                />
              </div>
            </div>

          </div>
        </div>
      </header>

      {/* ── Main content ── */}
      <main className="flex-1 w-full max-w-2xl mx-auto px-4 py-5 space-y-4">

        {/* Verification status card */}
        <section className={`rounded-xl border-2 p-4 ${isVerified ? 'bg-green-50 border-green-400' : 'bg-yellow-50 border-yellow-300'}`}>
          <div className="flex items-start gap-3">
            {isVerified ? (
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            )}
            <div className="min-w-0">
              <h3 className={`text-sm font-bold mb-1 ${isVerified ? 'text-green-800' : 'text-yellow-800'}`}>
                {isVerified
                  ? 'This translation has been certified and completed'
                  : 'This project exists but has no completed translations yet'}
              </h3>
              <p className={`text-xs leading-relaxed ${isVerified ? 'text-green-700' : 'text-yellow-700'}`}>
                {isVerified
                  ? `${translations.length} translation${translations.length > 1 ? 's' : ''} verified in our system. Document details are shown below.`
                  : 'The translation may still be in progress. Please check back later or contact support.'}
              </p>
            </div>
          </div>
        </section>

        {/* Document details */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Document Details</h3>
          </div>
          <dl className="divide-y divide-gray-100">
            <div className="px-4 py-3">
              <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Document Holder</dt>
              <dd className="text-sm font-medium text-gray-900 break-words">{documentHolder}</dd>
            </div>
            <div className="px-4 py-3">
              <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Languages</dt>
              <dd className="text-sm font-medium text-gray-900">{sourceLanguage} → {targetLanguage}</dd>
            </div>
            <div className="px-4 py-3">
              <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Certified Translator</dt>
              <dd className="text-sm font-medium text-gray-900">{translatorName}</dd>
            </div>
            {completedAt && (
              <div className="px-4 py-3">
                <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Certification Date</dt>
                <dd className="text-sm font-medium text-gray-900">{formatDate(completedAt)}</dd>
              </div>
            )}
          </dl>
        </section>

        {/* Full PDF integrity verification */}
        {project?.hasFinalDocument && (
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Verify Your PDF</h3>
            </div>
            <div className="px-4 py-4 space-y-3">
              {finalPdfSha256 ? (
                <>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Select the PDF you received. Your browser computes its SHA-256 fingerprint locally and compares it
                    with the final signed PDF stored by juree. The file is not uploaded.
                  </p>
                  <label className="block">
                    <span className="sr-only">Choose PDF to verify</span>
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={handlePdfVerification}
                      className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-slate-800"
                    />
                  </label>

                  {pdfCheck.status === 'checking' && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-3 text-sm text-blue-800">
                      Checking {pdfCheck.fileName}...
                    </div>
                  )}

                  {pdfCheck.status === 'match' && (
                    <div className="rounded-lg border border-green-300 bg-green-50 px-3 py-3">
                      <p className="text-sm font-semibold text-green-800">Document integrity verified</p>
                      <p className="text-xs text-green-700 mt-1">
                        This PDF matches the final signed document stored in juree.
                      </p>
                    </div>
                  )}

                  {pdfCheck.status === 'mismatch' && (
                    <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-3">
                      <p className="text-sm font-semibold text-red-800">Document does not match our stored final PDF</p>
                      <p className="text-xs text-red-700 mt-1">
                        The selected PDF may be a different version or may have been altered.
                      </p>
                      {pdfCheck.hash && (
                        <p className="font-mono text-[11px] text-red-700 break-all mt-2">{pdfCheck.hash}</p>
                      )}
                    </div>
                  )}

                  {pdfCheck.status === 'error' && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-800">
                      {pdfCheck.error}
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-3">
                  <p className="text-sm font-semibold text-amber-900">Limited verification available</p>
                  <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                    This older document was created before full PDF fingerprints were stored. The project record,
                    translation status, and certificate content hash can still be reviewed below.
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Digital signature verification */}
        {project?.hasFinalDocument && (
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Digital Signature</h3>
            </div>
            <div className="px-4 py-4 space-y-3">
              {signatureStatus === 'valid' && (
                <div className="rounded-lg border border-green-300 bg-green-50 px-3 py-3">
                  <p className="text-sm font-semibold text-green-800">Digital signature valid</p>
                  <p className="text-xs text-green-700 mt-1">
                    The embedded PDF signature cryptographically matches the signed document bytes.
                  </p>
                </div>
              )}

              {signatureStatus === 'invalid' && (
                <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-3">
                  <p className="text-sm font-semibold text-red-800">Digital signature invalid</p>
                  <p className="text-xs text-red-700 mt-1">
                    {signatureVerification?.error || 'The embedded PDF signature could not be verified.'}
                  </p>
                </div>
              )}

              {signatureStatus === 'unsigned' && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-3">
                  <p className="text-sm font-semibold text-amber-900">Document is unsigned</p>
                  <p className="text-xs text-amber-800 mt-1">
                    No embedded PDF digital signature was found for this final document.
                  </p>
                </div>
              )}

              {signatureStatus === 'unsupported' && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-3">
                  <p className="text-sm font-semibold text-amber-900">Signature verification unsupported</p>
                  <p className="text-xs text-amber-800 mt-1">
                    {signatureVerification?.error || 'This PDF signature format is not supported by this verification page.'}
                  </p>
                </div>
              )}

              {!signatureStatus && (
                <div className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-3">
                  <p className="text-sm font-semibold text-slate-800">Signature verification not available</p>
                  <p className="text-xs text-slate-600 mt-1">
                    This document was created before digital signature verification results were stored.
                  </p>
                </div>
              )}

              {signatureVerification?.signerName && (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <dt className="font-semibold text-gray-400 uppercase tracking-wide">Signer</dt>
                    <dd className="text-gray-900 break-words">{signatureVerification.signerName}</dd>
                  </div>
                  {signatureVerification.issuerName && (
                    <div>
                      <dt className="font-semibold text-gray-400 uppercase tracking-wide">Issuer</dt>
                      <dd className="text-gray-900 break-words">{signatureVerification.issuerName}</dd>
                    </div>
                  )}
                  {signatureVerification.certificateSerial && (
                    <div>
                      <dt className="font-semibold text-gray-400 uppercase tracking-wide">Certificate Serial</dt>
                      <dd className="font-mono text-gray-900 break-all">{signatureVerification.certificateSerial}</dd>
                    </div>
                  )}
                  {signatureVerification.certificateValidTo && (
                    <div>
                      <dt className="font-semibold text-gray-400 uppercase tracking-wide">Certificate Valid Until</dt>
                      <dd className="text-gray-900">{formatDate(signatureVerification.certificateValidTo)}</dd>
                    </div>
                  )}
                </dl>
              )}

              <p className="text-xs text-gray-500 leading-relaxed">
                Certificate trust chain and revocation status are not validated by this page.
              </p>
            </div>
          </section>
        )}

        {/* Signed final document preview + download */}
        {project?.hasFinalDocument && signedDownloadUrl && signedPreviewUrl && (
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Signed Translation</h3>
            </div>
            <div className="px-4 py-4 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {project?.finalDocument?.fileName || 'signed_translation.pdf'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Latest signed version{project?.finalDocument?.version ? ` (v${project.finalDocument.version})` : ''}
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-100 overflow-hidden">
                <object
                  data={`${signedPreviewUrl}#toolbar=1`}
                  type="application/pdf"
                  className="w-full h-[420px] sm:h-[560px] md:h-[640px] bg-gray-50"
                  aria-label="Signed Translation Preview"
                >
                  <div className="m-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    <p className="mb-3">
                      Inline preview is not available in this browser. You can still download the signed PDF.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <a
                        href={signedDownloadUrl}
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
                      >
                        Download
                      </a>
                    </div>
                  </div>
                </object>
              </div>
            </div>
          </section>
        )}

        {/* Certified documents list */}
        {translations.length > 0 && (
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Certified Documents ({translations.length})
              </h3>
            </div>
            <ul className="divide-y divide-gray-100">
              {translations.map((t) => (
                <li key={t.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {t.originalFileName || `Document ${t.id?.slice(-6)}`}
                    </p>
                    {t.documentType && (
                      <p className="text-xs text-gray-400 capitalize">{t.documentType.replace(/_/g, ' ')}</p>
                    )}
                  </div>
                  <span className="flex-shrink-0 inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-medium px-2 py-1 rounded-full">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Certified
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Certificate content hash */}
        {hashFromUrl && (
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Certificate Content Hash</h3>
            </div>
            <div className="px-4 py-4">
              <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                The SHA-256 hash below is embedded in the QR code on your authentication certificate page. It identifies
                the translated content pages before the authentication certificate page and digital signature are added.
              </p>
              <div className="bg-gray-900 rounded-lg px-3 py-3 overflow-x-auto">
                <p className="font-mono text-xs text-green-400 break-all select-all leading-relaxed">{hashFromUrl}</p>
              </div>
              <p className="text-xs text-gray-400 mt-2">Tap and hold the hash above to copy it.</p>
            </div>
          </section>
        )}

        {/* What this page confirms */}
        <section className="bg-blue-50 rounded-xl border border-blue-200 px-4 py-4">
          <h3 className="text-xs font-semibold text-blue-900 uppercase tracking-wide mb-2">What this page confirms</h3>
          <ul className="text-xs text-blue-800 space-y-1.5 list-disc list-inside leading-relaxed">
            <li>The project and translation record exist in juree's system.</li>
            <li>The translation was carried out by a certified juree translator.</li>
            <li>The status of the translation is <span className="font-semibold">COMPLETED</span>.</li>
            <li>The Verify Your PDF panel can compare a selected PDF with the final stored document fingerprint.</li>
            <li>The Digital Signature panel reports cryptographic PDF signature verification when available.</li>
            <li>The certificate content hash is the same one printed on your authentication certificate page.</li>
          </ul>
          <p className="text-xs text-blue-600 mt-3 leading-relaxed">
            Full PDF integrity verification happens locally in your browser. The selected file is not uploaded.
          </p>
        </section>

      </main>

      {/* ── Footer ── */}
      <footer className="bg-white border-t border-gray-200 py-4 mt-auto">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left">
            <div>
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-0.5">
                <span className="text-sm font-bold text-blue-600">juree</span>
                <span className="text-gray-300">|</span>
                <span className="text-xs text-gray-500">Certified Translation Services</span>
              </div>
              <p className="text-xs text-gray-400">36 RUE SAINT DIDIER, 75116 PARIS, FRANCE</p>
            </div>
            <div className="text-center sm:text-right">
              <a href="tel:+33171736436" className="text-xs text-gray-500 hover:text-blue-600 transition-colors">
                +33 (1) 71 73 64 36
              </a>
              <p className="text-xs text-gray-400">&copy; {new Date().getFullYear()} juree Inc.</p>
            </div>
          </div>
          <p className="text-xs text-center text-gray-400 mt-3 pt-3 border-t border-gray-100">
            This verification page is publicly accessible. Document content is not displayed to protect privacy.
          </p>
        </div>
      </footer>

    </div>
  );
};

export default TranslationVerificationPage;
